// KHLIFI HOUCEM / FORMATEUR CLOUD && DEVSECOPS
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { interviewAPI } from "@/lib/api";
import { WorkspaceLayout } from "@/components/layout/WorkspaceLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  ChevronLeft, Loader2, Save, Mail, Phone, Briefcase, Calendar,
  Play, Clock, Plus
} from "lucide-react";
import { toast } from "sonner";
import { getInitials, formatDate } from "@/lib/utils";

const PIPELINE_LABELS = { to_plan: "À planifier", planned: "Planifié", completed: "Réalisé", validated: "Validé", rejected: "Rejeté", on_hold: "En attente" };
const PIPELINE_COLORS = { to_plan: "bg-gray-100 text-gray-700", planned: "bg-blue-100 text-blue-700", completed: "bg-purple-100 text-purple-700", validated: "bg-green-100 text-green-700", rejected: "bg-red-100 text-red-700", on_hold: "bg-yellow-100 text-yellow-700" };
const LEVEL_LABELS = { junior: "Junior", confirmed: "Confirmé", senior: "Senior", expert: "Expert" };
const REC_LABELS = { rejected: "Rejeté", review: "À revoir", retained: "Retenu", excellent: "Excellent" };
const REC_COLORS = { rejected: "bg-red-100 text-red-700", review: "bg-yellow-100 text-yellow-700", retained: "bg-green-100 text-green-700", excellent: "bg-emerald-100 text-emerald-700" };
const STATUS_LABELS = { draft: "Brouillon", in_progress: "En cours", completed: "Terminé", cancelled: "Annulé" };

export default function InterviewCandidatePage() {
  const { workspaceId, candidateId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [candidate, setCandidate] = useState(null);
  const [interviews, setInterviews] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newInterviewDialog, setNewInterviewDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("");

  useEffect(() => { loadData(); }, [candidateId]);

  const loadData = async () => {
    try {
      const [cRes, iRes, tRes] = await Promise.all([
        interviewAPI.getCandidate(candidateId),
        interviewAPI.listInterviews(workspaceId, { candidate_id: candidateId }),
        interviewAPI.listTemplates(workspaceId, { status: "published" }),
      ]);
      setCandidate(cRes.data);
      setInterviews(iRes.data);
      setTemplates(tRes.data);
    } catch (e) { toast.error("Erreur"); navigate(-1); } finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await interviewAPI.updateCandidate(candidateId, candidate);
      toast.success("Candidat mis à jour");
    } catch (e) { toast.error("Erreur"); } finally { setSaving(false); }
  };

  const handleStartInterview = async () => {
    if (!selectedTemplate) { toast.error("Sélectionnez un template"); return; }
    try {
      const res = await interviewAPI.createInterview(workspaceId, user?.id, {
        candidate_id: candidateId, template_id: selectedTemplate,
      });
      toast.success("Entretien créé");
      navigate(`/workspace/${workspaceId}/interviews/live/${res.data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  if (loading || !candidate) {
    return <WorkspaceLayout><div className="page-container"><div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div></div></WorkspaceLayout>;
  }

  return (
    <WorkspaceLayout>
      <div className="page-container animate-fade-in">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/workspace/${workspaceId}/interviews/candidates`)}>
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <Avatar className="h-14 w-14">
            <AvatarFallback className="bg-primary/10 text-primary text-lg font-bold">
              {getInitials(candidate.full_name?.split(" ")[0], candidate.full_name?.split(" ").slice(1).join(" "))}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <h1 className="text-2xl font-semibold">{candidate.full_name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge className={PIPELINE_COLORS[candidate.pipeline_status]}>{PIPELINE_LABELS[candidate.pipeline_status]}</Badge>
              {candidate.target_role && <span className="text-sm text-muted-foreground">{candidate.target_role}</span>}
            </div>
          </div>
          <Button onClick={() => setNewInterviewDialog(true)} className="gap-2"><Play className="h-4 w-4" />Nouvel entretien</Button>
          <Button variant="outline" onClick={handleSave} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}</Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Candidate Info */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader><CardTitle>Informations</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Nom complet</Label><Input value={candidate.full_name} onChange={(e) => setCandidate({...candidate, full_name: e.target.value})} /></div>
                  <div><Label>Email</Label><Input value={candidate.email} onChange={(e) => setCandidate({...candidate, email: e.target.value})} /></div>
                  <div><Label>Téléphone</Label><Input value={candidate.phone} onChange={(e) => setCandidate({...candidate, phone: e.target.value})} /></div>
                  <div><Label>Poste visé</Label><Input value={candidate.target_role} onChange={(e) => setCandidate({...candidate, target_role: e.target.value})} /></div>
                  <div><Label>Expérience (ans)</Label><Input type="number" value={candidate.experience_years} onChange={(e) => setCandidate({...candidate, experience_years: parseInt(e.target.value) || 0})} /></div>
                  <div>
                    <Label>Niveau estimé</Label>
                    <Select value={candidate.estimated_level} onValueChange={(v) => setCandidate({...candidate, estimated_level: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.entries(LEVEL_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Statut pipeline</Label>
                    <Select value={candidate.pipeline_status} onValueChange={(v) => setCandidate({...candidate, pipeline_status: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.entries(PIPELINE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Notes</Label><Textarea value={candidate.notes} onChange={(e) => setCandidate({...candidate, notes: e.target.value})} rows={3} /></div>
              </CardContent>
            </Card>
          </div>

          {/* Right: Interview History */}
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Historique entretiens</CardTitle>
                  <Badge variant="secondary">{interviews.length}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {interviews.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">Aucun entretien</p>
                ) : (
                  interviews.map((itw) => (
                    <div key={itw.id} className="p-3 rounded-lg border hover:border-primary/50 transition-colors cursor-pointer"
                      onClick={() => {
                        if (itw.status === "completed") navigate(`/workspace/${workspaceId}/interviews/summary/${itw.id}`);
                        else navigate(`/workspace/${workspaceId}/interviews/live/${itw.id}`);
                      }}>
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-sm">{itw.template_title}</p>
                        <Badge variant="outline" className="text-xs">{STATUS_LABELS[itw.status]}</Badge>
                      </div>
                      {itw.percentage > 0 && (
                        <div className="mt-2 flex items-center gap-2">
                          <div className="flex-1 bg-muted rounded-full h-2">
                            <div className="bg-primary rounded-full h-2 transition-all" style={{ width: `${Math.min(itw.percentage, 100)}%` }} />
                          </div>
                          <span className="text-xs font-medium">{itw.percentage.toFixed(0)}%</span>
                        </div>
                      )}
                      {itw.recommendation && (
                        <Badge className={`mt-2 text-xs ${REC_COLORS[itw.recommendation_override || itw.recommendation] || ""}`}>
                          {REC_LABELS[itw.recommendation_override || itw.recommendation]}
                        </Badge>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        {itw.interviewer_name} · {itw.created_at ? formatDate(itw.created_at) : ""}
                      </p>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* New Interview Dialog */}
      <Dialog open={newInterviewDialog} onOpenChange={setNewInterviewDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Démarrer un entretien</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Template d'entretien</Label>
              <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                <SelectTrigger><SelectValue placeholder="Choisir un template..." /></SelectTrigger>
                <SelectContent>
                  {templates.map(t => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.title} — {t.target_role} ({t.duration_minutes} min)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {templates.length === 0 && (
                <p className="text-sm text-muted-foreground mt-2">Aucun template publié. <Button variant="link" className="p-0 h-auto" onClick={() => navigate(`/workspace/${workspaceId}/interviews/templates`)}>Créer un template</Button></p>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setNewInterviewDialog(false)}>Annuler</Button>
              <Button onClick={handleStartInterview} disabled={!selectedTemplate} className="gap-2"><Play className="h-4 w-4" />Démarrer</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </WorkspaceLayout>
  );
}
